import Grid from "@mui/material/Grid2"
import { Card, Box, Typography, CircularProgress } from "@mui/material"
import { DetailTable } from "@/components/Tables/DetailTable"
import { columns as orderColumns } from "@/pages/orders/Columns"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import { formatToDevanagariStyle } from "@/utilities/helper"
import { BrandSelect } from "@/components/Reports/Brand/BrandSelect"
import { useInitBrands } from "@/hooks/useInitBrands"
import { useAppSelector } from "@/lib/hooks"
import { useEffect } from "react"

const Dashboard = () => {
  const { data: productCountResponse, isLoading: productCountLoading } =
    useCustomQuery(["product-count"], "product/get-count")
  const { data: supplierCountResponse, isLoading: supplierCountLoading } =
    useCustomQuery(["supplier-count"], "supplier/get-count")
  const { data: customerCountResponse, isLoading: customerCountLoading } =
    useCustomQuery(["customer-count"], "customer/get-count")

  useInitBrands()
  const selectedBrandId = useAppSelector((state) => state.brand.selectedBrandId)
  const {
    data: paymentMethodRevenueResponse,
    isLoading: paymentMethodRevenueLoading,
    refetch: refetchPaymentRevenue,
  } = useCustomQuery(
    ["payment-method-revenue"],
    `report/revenue-by-payment-method?brand_id=${selectedBrandId}`
  )

  const {
    data: totalSalesReturnResponse,
    isLoading: totalSalesReturnLoading,
    refetch: refetchSalesReturn,
  } = useCustomQuery(
    ["total-sales-return"],
    `report/total-sales-return?brand_id=${selectedBrandId}`
  )

  useEffect(() => {
    if (selectedBrandId !== undefined) {
      refetchPaymentRevenue()
      refetchSalesReturn()
    }
  }, [selectedBrandId])

  const { data: totalStockResponse, isLoading: totalStockLoading } =
    useCustomQuery(["total-stock"], "report/total-stock")

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          mb: 3,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            textTransform: "uppercase",
          }}
        >
          Today's Sales
        </Typography>

        <Box sx={{ width: { xs: "100%", sm: 400 } }}>
          <BrandSelect />
        </Box>
      </Box>

      <Grid
        container
        spacing={3}
        sx={{
          mb: { xs: 1.5, sm: 3 },
        }}
      >
        {paymentMethodRevenueResponse?.length > 0 ? (
          paymentMethodRevenueResponse?.map((paymentMethod) => (
            <Grid
              key={paymentMethod.payment_method_id}
              xs={12}
              sm={6}
              md={4}
              lg={3}
            >
              <DashboardCard
                label={paymentMethod.payment_method_name}
                count={`Rs. ${formatToDevanagariStyle(paymentMethod.total_revenue)}`}
                isLoading={paymentMethodRevenueLoading}
              />
            </Grid>
          ))
        ) : (
          <Grid xs={12} sm={6} md={4} lg={3}>
            <DashboardCard
              label={"Cash"}
              count={`Rs. 0`}
              isLoading={paymentMethodRevenueLoading}
            />
          </Grid>
        )}
        <Grid xs={12} sm={6} md={4} lg={3}>
          <DashboardCard
            label={"Sales Return"}
            count={`Rs. ${formatToDevanagariStyle(totalSalesReturnResponse?.totalSalesReturn)}`}
            isLoading={totalSalesReturnLoading}
          />
        </Grid>
      </Grid>

      <Grid
        container
        spacing={3}
        sx={{
          mb: { xs: 1.5, sm: 3 },
        }}
      >
        <Grid xs={12} sm={6} md={4} lg={3}>
          <DashboardCard
            label="Total Stock"
            count={`${totalStockResponse?.totalStock ?? "-"}`}
            isLoading={totalStockLoading}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} lg={3}>
          <DashboardCard
            label="Total Products"
            count={productCountResponse?.data?.count ?? "-"}
            isLoading={productCountLoading}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} lg={3}>
          <DashboardCard
            label="Total Suppliers"
            count={supplierCountResponse?.data?.count ?? "-"}
            isLoading={supplierCountLoading}
          />
        </Grid>
        <Grid xs={12} sm={6} md={4} lg={3}>
          <DashboardCard
            label="Total Customers"
            count={customerCountResponse?.data?.count ?? "-"}
            isLoading={customerCountLoading}
          />
        </Grid>
      </Grid>
    </>
  )
}
const DashboardCard = ({ label, count, isLoading = false }) => {
  return (
    <Card
      sx={{
        p: { xs: 1.5, sm: 3 },
        borderRadius: { xs: 2, sm: 3 },
        boxShadow: 3,
        textAlign: "center",
        minHeight: { xs: 70, sm: 100 },
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
      }}
      className="rmui-card"
    >
      <Typography
        component="span"
        sx={{
          mb: 0.5,
          fontWeight: "medium",
          textTransform: "capitalize",
          color: "text.secondary",
          fontSize: { xs: 12, sm: 16 },
        }}
      >
        {label}
      </Typography>

      <Typography
        variant="h5"
        sx={{
          fontWeight: 700,
          fontSize: { xs: 16, sm: 24 },
          color: "text.primary",
        }}
      >
        {isLoading ? <CircularProgress size={18} /> : count}
      </Typography>
    </Card>
  )
}

export default Dashboard
